import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  TextField,
  Paper,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';

// Configure PDF.js worker
// Bug fix: Use unpkg CDN which mirrors npm packages directly (cdnjs may not have all versions)
// Bug fix: Use .min.mjs extension for ES module worker (required for pdfjs-dist 4.x+)
const PDFJS_ASSET_BASE_URL = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}`;
pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_ASSET_BASE_URL}/build/pdf.worker.min.mjs`;
const PDFJS_CMAP_URL = `${PDFJS_ASSET_BASE_URL}/cmaps/`;
const PDFJS_STANDARD_FONT_DATA_URL = `${PDFJS_ASSET_BASE_URL}/standard_fonts/`;

export interface SearchResult {
  page: number;
  rect: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  text: string;
}

// Internal interface for storing PDF coordinates (scale-independent)
interface PDFSearchResult {
  page: number;
  pdfRect: [number, number, number, number]; // [x1, y1, x2, y2] in PDF coordinates
  text: string;
}

interface PDFHighlightViewerProps {
  pdfUrl: string;
  highlightTerms?: string[];
  initialPage?: number;
  initialPageExplicit?: boolean;
  preferredSearchPage?: number;
  onSearchResults?: (results: SearchResult[]) => void;
  /** Display mode: 'single' for page-by-page, 'scroll' for continuous scroll (default: 'scroll') */
  displayMode?: 'single' | 'scroll';
  /** Number of pages to preload before/after visible pages in scroll mode (default: 2) */
  lazyLoadBuffer?: number;
}

const PDFHighlightViewer: React.FC<PDFHighlightViewerProps> = ({
  pdfUrl,
  highlightTerms = [],
  initialPage = 1,
  initialPageExplicit = false,
  preferredSearchPage,
  onSearchResults,
  displayMode = 'scroll',
  lazyLoadBuffer = 2,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(highlightTerms.join(' '));
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [pdfSearchResults, setPdfSearchResults] = useState<PDFSearchResult[]>([]); // Store PDF coordinates
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [showSearchBar, setShowSearchBar] = useState(highlightTerms.length > 0);
  const [showResultsSidebar, setShowResultsSidebar] = useState(false);

  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const highlightLayerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Scroll mode: track which pages are visible/should be rendered
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set());
  // Scroll mode: track which pages have been rendered
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const renderingPagesRef = useRef<Set<number>>(new Set());
  // Scroll mode: refs for page containers (for IntersectionObserver)
  const pageContainerRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Scroll mode: IntersectionObserver for lazy loading
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Bug fix: Use ref to track pdfDoc for cleanup (avoids stale closure)
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  // Bug fix: Track loading task for cancellation on URL change
  const loadingTaskRef = useRef<pdfjsLib.PDFDocumentLoadingTask | null>(null);
  // Bug fix: Track if initial search has been performed to avoid re-triggering on zoom
  const initialSearchPerformedRef = useRef(false);
  // Bug fix: Track last highlight terms to detect changes
  const lastHighlightTermsRef = useRef<string>('');
  // Bug fix: Track if page rendering is complete for proper highlight timing
  const [pageRenderComplete, setPageRenderComplete] = useState(false);
  // Bug fix: Track initialPage ref to handle page changes without PDF reload
  const initialPageRef = useRef(initialPage);
  const initialPageExplicitRef = useRef(initialPageExplicit);
  const preferredSearchPageRef = useRef(preferredSearchPage);
  const pendingInitialScrollPageRef = useRef<number | null>(
    initialPage > 1 ? initialPage : null
  );
  // Bug fix: Track if a search is currently in progress for cancellation
  const searchAbortControllerRef = useRef<AbortController | null>(null);

  const scrollToPage = useCallback((
    pageNum: number,
    behavior: ScrollBehavior = 'auto',
    block: 'start' | 'center' = 'start',
  ) => {
    const container = containerRef.current;
    const pageContainer = pageContainerRefs.current.get(pageNum);
    if (!container || !pageContainer) return;

    let top = pageContainer.offsetTop;
    if (block === 'center') {
      top = pageContainer.offsetTop - Math.max(
        0,
        (container.clientHeight - pageContainer.offsetHeight) / 2,
      );
    }

    const maxScrollTop = container.scrollHeight - container.clientHeight;
    const clampedTop = maxScrollTop > 0
      ? Math.max(0, Math.min(top, maxScrollTop))
      : Math.max(0, top);

    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: clampedTop, behavior });
    } else {
      container.scrollTop = clampedTop;
    }
  }, []);

  // Load PDF document
  // Bug fix: Removed initialPage from dependencies to prevent unnecessary PDF reloads
  useEffect(() => {
    let isCancelled = false;
    // Capture refs at effect start for cleanup (ESLint react-hooks/exhaustive-deps)
    const canvasRefsMap = canvasRefs.current;
    const highlightLayerRefsMap = highlightLayerRefs.current;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        // Bug fix: Clear search results when loading new document
        setSearchResults([]);
        setPdfSearchResults([]);
        setRenderedPages(new Set());
        setVisiblePages(new Set());
        renderedPagesRef.current.clear();
        renderingPagesRef.current.clear();

        // Bug fix: Cancel previous loading task if exists
        if (loadingTaskRef.current) {
          loadingTaskRef.current.destroy();
          loadingTaskRef.current = null;
        }

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          cMapUrl: PDFJS_CMAP_URL,
          cMapPacked: true,
          standardFontDataUrl: PDFJS_STANDARD_FONT_DATA_URL,
          useSystemFonts: true,
        });
        loadingTaskRef.current = loadingTask;

        const pdf = await loadingTask.promise;

        // Bug fix: Check if cancelled before setting state
        if (isCancelled) {
          pdf.destroy();
          return;
        }

        // Bug fix: Cleanup previous document if exists
        if (pdfDocRef.current) {
          pdfDocRef.current.destroy();
        }

        setPdfDoc(pdf);
        pdfDocRef.current = pdf;
        setTotalPages(pdf.numPages);

        // Bug fix: Use ref to get current initialPage value (avoids needing it in deps)
        const targetPage = initialPageRef.current;
        // Bug fix: Validate initialPage and fallback to 1 if invalid
        // Handle all invalid cases: < 1 or > numPages
        if (targetPage >= 1 && targetPage <= pdf.numPages) {
          setCurrentPage(targetPage);
        } else {
          // Invalid page number (< 1 or > numPages) - fallback to page 1
          if (targetPage !== 1) {
            console.warn(`Invalid initialPage ${targetPage}, falling back to page 1`);
          }
          setCurrentPage(1);
        }
      } catch (err) {
        // Bug fix: Ignore cancellation errors
        if (isCancelled) return;
        console.error('Error loading PDF:', err);
        setError(t('documents.pdfLoadFailed'));
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    if (pdfUrl) {
      loadPDF();
    }

    return () => {
      // Bug fix: Mark as cancelled and cleanup using ref
      isCancelled = true;

      // Bug fix: Cancel any in-progress search BEFORE destroying PDF document
      // This prevents search operations from failing with destroyed document errors
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }

      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
        loadingTaskRef.current = null;
      }

      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }

      // Bug fix: Clear canvas and highlight layer refs to prevent memory leak
      // Use captured refs from effect start for proper cleanup
      canvasRefsMap.clear();
      highlightLayerRefsMap.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't reload the PDF and reset the current page (PR #790)
  }, [pdfUrl]);

  // Bug fix: Update initialPage ref and navigate when initialPage prop changes (without reloading PDF)
  useEffect(() => {
    initialPageRef.current = initialPage;
    initialPageExplicitRef.current = initialPageExplicit;
    preferredSearchPageRef.current = preferredSearchPage;
    if (initialPage > 1) {
      pendingInitialScrollPageRef.current = initialPage;
    }
    // Navigate to new page if PDF is loaded and page is valid
    if (pdfDoc && initialPage >= 1 && initialPage <= totalPages) {
      setCurrentPage(initialPage);
      if (displayMode === 'scroll') {
        scrollToPage(initialPage, 'auto', 'start');
      }
    }
  }, [initialPage, initialPageExplicit, preferredSearchPage, pdfDoc, totalPages, displayMode, scrollToPage]);

  // Render current page
  // Bug fix: Accept shouldContinue callback to prevent stale renders from modifying DOM
  const renderPage = useCallback(async (pageNum: number, shouldContinue: () => boolean = () => true) => {
    if (!pdfDoc || !containerRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageNum);

      // Bug fix: Check if render is still valid after async operation
      if (!shouldContinue()) return;

      const viewport = page.getViewport({ scale });

      // Get or create canvas
      let canvas = canvasRefs.current.get(pageNum);
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvasRefs.current.set(pageNum, canvas);
      }

      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render PDF page
      await page.render({
        canvasContext: context,
        viewport,
      }).promise;

      // Bug fix: Check if render is still valid after PDF render completes
      if (!shouldContinue()) return;

      // Clear container and add canvas
      const wrapper = containerRef.current;
      if (wrapper) {
        // Find or create page container
        let pageContainer = wrapper.querySelector(`[data-page="${pageNum}"]`) as HTMLDivElement;
        if (!pageContainer) {
          pageContainer = document.createElement('div');
          pageContainer.setAttribute('data-page', String(pageNum));
          pageContainer.style.position = 'relative';
          pageContainer.style.flexShrink = '0';
          pageContainer.style.marginBottom = '20px';
          pageContainer.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)';
          pageContainer.style.background = 'white';
          wrapper.appendChild(pageContainer);
        }

        // Clear and add canvas
        pageContainer.innerHTML = '';
        pageContainer.style.width = `${viewport.width}px`;
        pageContainer.style.height = `${viewport.height}px`;
        // Ensure canvas is positioned at (0,0) with no margins
        canvas.style.display = 'block';
        canvas.style.margin = '0';
        canvas.style.padding = '0';
        pageContainer.appendChild(canvas);

        // Create highlight layer
        let highlightLayer = highlightLayerRefs.current.get(pageNum);
        if (!highlightLayer) {
          highlightLayer = document.createElement('div');
          highlightLayer.className = 'highlight-layer';
          highlightLayer.style.position = 'absolute';
          highlightLayer.style.top = '0';
          highlightLayer.style.left = '0';
          highlightLayer.style.right = '0';
          highlightLayer.style.bottom = '0';
          highlightLayer.style.pointerEvents = 'none';
          highlightLayerRefs.current.set(pageNum, highlightLayer);
        }
        highlightLayer.innerHTML = '';
        pageContainer.appendChild(highlightLayer);
      }
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  }, [pdfDoc, scale]);

  // Scroll mode: Render page for scroll view (into existing placeholder container)
  const renderScrollPage = useCallback(async (pageNum: number, shouldContinue: () => boolean = () => true) => {
    if (!pdfDoc || !containerRef.current) return;

    const pageContainer = pageContainerRefs.current.get(pageNum);
    if (!pageContainer) return;

    // Check synchronously; React state updates are too late to prevent
    // overlapping pdf.js render tasks from reusing the same canvas.
    if (
      renderedPagesRef.current.has(pageNum) ||
      renderingPagesRef.current.has(pageNum)
    ) {
      return;
    }
    renderingPagesRef.current.add(pageNum);

    try {
      const page = await pdfDoc.getPage(pageNum);
      if (!shouldContinue()) return;

      const viewport = page.getViewport({ scale });

      // Get or create canvas
      let canvas = canvasRefs.current.get(pageNum);
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvasRefs.current.set(pageNum, canvas);
      }

      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render PDF page
      await page.render({
        canvasContext: context,
        viewport,
      }).promise;

      if (!shouldContinue()) return;

      // Update container size and clear placeholder styling
      pageContainer.style.width = `${viewport.width}px`;
      pageContainer.style.height = `${viewport.height}px`;
      // Remove flex centering used for placeholder
      pageContainer.style.display = 'block';
      pageContainer.style.alignItems = '';
      pageContainer.style.justifyContent = '';
      pageContainer.style.background = 'white';

      // Clear placeholder content and add canvas
      const existingCanvas = pageContainer.querySelector('canvas');
      if (!existingCanvas) {
        // Remove placeholder div if exists
        const placeholder = pageContainer.querySelector('div:not(.highlight-layer)');
        if (placeholder) {
          placeholder.remove();
        }
        // Ensure canvas is positioned at (0,0) with no margins
        canvas.style.display = 'block';
        canvas.style.margin = '0';
        canvas.style.padding = '0';
        pageContainer.insertBefore(canvas, pageContainer.firstChild);
      }

      // Create highlight layer if not exists
      let highlightLayer = highlightLayerRefs.current.get(pageNum);
      if (!highlightLayer) {
        highlightLayer = document.createElement('div');
        highlightLayer.className = 'highlight-layer';
        highlightLayer.style.position = 'absolute';
        highlightLayer.style.top = '0';
        highlightLayer.style.left = '0';
        highlightLayer.style.right = '0';
        highlightLayer.style.bottom = '0';
        highlightLayer.style.pointerEvents = 'none';
        highlightLayerRefs.current.set(pageNum, highlightLayer);
        pageContainer.appendChild(highlightLayer);
      }

      // Mark as rendered
      renderedPagesRef.current.add(pageNum);
      setRenderedPages(new Set(renderedPagesRef.current));
    } catch (err) {
      console.error('Error rendering scroll page:', err);
    } finally {
      renderingPagesRef.current.delete(pageNum);
    }
  }, [pdfDoc, scale]);

  // Scroll mode: Set up page containers and IntersectionObserver
  useEffect(() => {
    if (displayMode !== 'scroll' || !pdfDoc || !containerRef.current || totalPages === 0) return;

    let isCancelled = false;

    // ESLint fix: Copy ref value to local variable for cleanup function
    const currentPageContainerRefs = pageContainerRefs.current;
    const currentRenderedPages = renderedPagesRef.current;
    const currentRenderingPages = renderingPagesRef.current;

    // Create placeholder containers for all pages
    const container = containerRef.current;
    container.innerHTML = '';

    // Get estimated page dimensions from first page
    (async () => {
      const firstPage = await pdfDoc.getPage(1);
      if (isCancelled) return;

      const viewport = firstPage.getViewport({ scale });
      const pageWidth = viewport.width;
      const pageHeight = viewport.height;

      // Create containers for all pages
      for (let i = 1; i <= totalPages; i++) {
        const pageContainer = document.createElement('div');
        pageContainer.setAttribute('data-page', String(i));
        pageContainer.style.position = 'relative';
        pageContainer.style.flexShrink = '0';
        pageContainer.style.width = `${pageWidth}px`;
        pageContainer.style.height = `${pageHeight}px`;
        pageContainer.style.marginBottom = '20px';
        pageContainer.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)';
        pageContainer.style.background = '#f0f0f0';
        pageContainer.style.display = 'flex';
        pageContainer.style.alignItems = 'center';
        pageContainer.style.justifyContent = 'center';

        // Add page number placeholder
        const placeholder = document.createElement('div');
        placeholder.style.color = '#999';
        placeholder.style.fontSize = '24px';
        placeholder.textContent = `Page ${i}`;
        pageContainer.appendChild(placeholder);

        container.appendChild(pageContainer);
        pageContainerRefs.current.set(i, pageContainer);
      }

      // Set up IntersectionObserver for lazy loading
      const observer = new IntersectionObserver(
        (entries) => {
          const newVisiblePages = new Set<number>();

          entries.forEach((entry) => {
            const pageNum = parseInt(entry.target.getAttribute('data-page') || '0', 10);
            if (entry.isIntersecting) {
              newVisiblePages.add(pageNum);
            }
          });

          // Update visible pages with buffer
          setVisiblePages((prev) => {
            const updated = new Set(prev);
            newVisiblePages.forEach((p) => {
              // Add page and buffer pages
              for (let i = Math.max(1, p - lazyLoadBuffer); i <= Math.min(totalPages, p + lazyLoadBuffer); i++) {
                updated.add(i);
              }
            });
            return updated;
          });
        },
        {
          root: container,
          rootMargin: '200px 0px', // Pre-load pages 200px before they're visible
          threshold: 0.1,
        }
      );

      observerRef.current = observer;

      // Observe all page containers
      pageContainerRefs.current.forEach((pageContainer) => {
        observer.observe(pageContainer);
      });

      // Initial scroll to target page if specified
      if (initialPage > 1 && initialPage <= totalPages) {
        pendingInitialScrollPageRef.current = initialPage;
        scrollToPage(initialPage, 'auto', 'start');
      }

      // Signal ready for highlights
      setPageRenderComplete(true);
    })();

    return () => {
      isCancelled = true;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      // ESLint fix: Use captured ref value instead of accessing .current in cleanup
      currentPageContainerRefs.clear();
      currentRenderedPages.clear();
      currentRenderingPages.clear();
      setRenderedPages(new Set());
      setVisiblePages(new Set());
    };
  }, [displayMode, pdfDoc, totalPages, scale, lazyLoadBuffer, initialPage, scrollToPage]);

  // Scroll mode: Render visible pages when they change
  useEffect(() => {
    if (displayMode !== 'scroll' || !pdfDoc) return;

    let isCancelled = false;

    // ESLint fix: Create shouldContinue callback outside the loop to avoid no-loop-func error
    const shouldContinue = () => !isCancelled;

    // Render all visible pages that haven't been rendered yet
    (async () => {
      const pagesToRender = Array.from(visiblePages)
        .filter(
          p =>
            !renderedPagesRef.current.has(p) &&
            !renderingPagesRef.current.has(p),
        )
        .sort((a, b) => a - b);

      for (const pageNum of pagesToRender) {
        if (isCancelled) break;
        await renderScrollPage(pageNum, shouldContinue);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [displayMode, pdfDoc, visiblePages, renderScrollPage]);

  useEffect(() => {
    if (displayMode !== 'scroll' || !pageRenderComplete) return;

    const pendingPage = pendingInitialScrollPageRef.current;
    if (!pendingPage || !renderedPages.has(pendingPage)) return;

    scrollToPage(pendingPage, 'auto', 'start');
    pendingInitialScrollPageRef.current = null;
  }, [displayMode, pageRenderComplete, renderedPages, scrollToPage]);

  // Single page mode: Render current page when it changes
  // Bug fix: Use async IIFE with cancellation to properly await renderPage and signal completion
  useEffect(() => {
    if (displayMode !== 'single') return;

    // Track if this effect instance is cancelled
    let isCancelled = false;

    // Bug fix: Create shouldContinue callback that checks cancellation status
    const shouldContinue = () => !isCancelled;

    if (pdfDoc) {
      // Mark render as incomplete before starting
      setPageRenderComplete(false);

      // Clear container
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }

      // Async IIFE to await renderPage with cancellation check
      (async () => {
        // Bug fix: Pass shouldContinue to renderPage to prevent stale renders from modifying DOM
        await renderPage(currentPage, shouldContinue);
        // Bug fix: Only update state if this render wasn't cancelled
        if (!isCancelled) {
          // Signal that rendering is complete so highlights can be drawn
          setPageRenderComplete(true);
        }
      })();
    }

    // Cleanup: mark this effect as cancelled so stale renders don't update state
    return () => {
      isCancelled = true;
    };
  }, [displayMode, pdfDoc, currentPage, scale, renderPage]);

  // Perform search - stores PDF coordinates for scale-independent rendering
  // Bug fix: Removed scale from dependencies to prevent re-running on zoom
  // Bug fix: Added AbortController for cancellation when PDF changes
  const performSearch = useCallback(async (
    query: string,
    navigateToFirst: boolean = true,
    usePreferredSearchPage: boolean = false,
  ) => {
    // Bug fix: Cancel any in-progress search
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    if (!pdfDoc || !query.trim()) {
      setSearchResults([]);
      setPdfSearchResults([]);
      return;
    }

    // Create new abort controller for this search
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    const pdfResults: PDFSearchResult[] = [];
    const searchTerms = query.toLowerCase().split(/\s+/).filter(t => t.length > 0);

    try {
      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        // Bug fix: Check if search was cancelled before processing each page
        if (abortController.signal.aborted) {
          return;
        }

        const page = await pdfDoc.getPage(pageNum);

        // Bug fix: Check if search was cancelled after async getPage
        if (abortController.signal.aborted) {
          return;
        }

        const textContent = await page.getTextContent();

        // Bug fix: Check if search was cancelled after async getTextContent
        if (abortController.signal.aborted) {
          return;
        }

        // Bug fix for Japanese PDFs: Text is often extracted as individual characters.
        // We need to concatenate adjacent text items and search across boundaries.

        // Collect all text items with their positions
        interface TextItemWithPosition {
          str: string;
          transform: number[];
          width: number;
          height: number;
          charIndex: number; // Index in concatenated string
        }

        const textItems: TextItemWithPosition[] = [];
        let fullText = '';

        for (const item of textContent.items) {
          if (!('str' in item) || !item.str) continue;

          textItems.push({
            str: item.str,
            transform: item.transform,
            width: item.width,
            height: item.height || 0,
            charIndex: fullText.length,
          });
          fullText += item.str;
        }

        const fullTextLower = fullText.toLowerCase();

        // Search for each term in the concatenated text
        for (const term of searchTerms) {
          let searchIndex = 0;
          let matchIndex: number;

          while ((matchIndex = fullTextLower.indexOf(term, searchIndex)) !== -1) {
            // Found a match! Find which text items contribute to this match
            const matchEnd = matchIndex + term.length;

            // Collect all text items that overlap with this match and compute merged bounding box
            let minX = Infinity;
            let minY = Infinity;
            let maxX = -Infinity;
            let maxY = -Infinity;
            let hasMatchingItems = false;

            for (const textItem of textItems) {
              const itemStart = textItem.charIndex;
              const itemEnd = itemStart + textItem.str.length;

              // Check if this item overlaps with the match
              if (itemStart < matchEnd && itemEnd > matchIndex) {
                hasMatchingItems = true;
                const transform = textItem.transform;
                const fontSize = Math.sqrt(transform[0] * transform[0] + transform[1] * transform[1]);

                const pdfX = transform[4];
                const pdfY = transform[5];
                const pdfW = textItem.width || fontSize * textItem.str.length * 0.6;
                const pdfH = fontSize;

                // Expand bounding box to include this text item
                minX = Math.min(minX, pdfX);
                minY = Math.min(minY, pdfY);
                maxX = Math.max(maxX, pdfX + pdfW);
                maxY = Math.max(maxY, pdfY + pdfH);
              }
            }

            // Create a single merged highlight for the entire match
            if (hasMatchingItems) {
              const pdfRect: [number, number, number, number] = [minX, minY, maxX, maxY];

              // Avoid duplicate highlights at same position
              const isDuplicate = pdfResults.some(r =>
                r.page === pageNum &&
                Math.abs(r.pdfRect[0] - pdfRect[0]) < 1 &&
                Math.abs(r.pdfRect[1] - pdfRect[1]) < 1
              );

              if (!isDuplicate) {
                pdfResults.push({
                  page: pageNum,
                  pdfRect,
                  text: fullText.substring(matchIndex, matchEnd),
                });
              }
            }

            searchIndex = matchIndex + 1;
          }
        }
      }

      // Bug fix: Check cancellation before setting results
      if (abortController.signal.aborted) {
        return;
      }

      // Convert to viewport coordinates for external consumers (use base scale for consistent results)
      const baseScale = 1.0;
      const viewportResults: SearchResult[] = await Promise.all(
        pdfResults.map(async (result) => {
          const page = await pdfDoc.getPage(result.page);
          const viewport = page.getViewport({ scale: baseScale });
          const viewRect = viewport.convertToViewportRectangle(result.pdfRect);

          const minX = Math.min(viewRect[0], viewRect[2]);
          const minY = Math.min(viewRect[1], viewRect[3]);
          const width = Math.abs(viewRect[0] - viewRect[2]);
          const height = Math.abs(viewRect[1] - viewRect[3]);

          return {
            page: result.page,
            rect: { x: minX, y: minY, w: width, h: height },
            text: result.text,
          };
        })
      );

      // Bug fix: Check cancellation after viewport conversion
      if (abortController.signal.aborted) {
        return;
      }

      // Bug fix: Update both arrays atomically to prevent inconsistency on cancellation
      // Both pdfSearchResults and searchResults must be set together
      setPdfSearchResults(pdfResults);
      setSearchResults(viewportResults);
      setCurrentResultIndex(0);
      onSearchResults?.(viewportResults);

      // Navigate to first result if any (only if navigateToFirst is true)
      if (navigateToFirst && pdfResults.length > 0) {
        const preferredPage = usePreferredSearchPage ? preferredSearchPageRef.current : undefined;
        const targetIndex = preferredPage && preferredPage > 0
          ? pdfResults.reduce((bestIndex, result, index) => {
              const best = pdfResults[bestIndex];
              const distance = Math.abs(result.page - preferredPage);
              const bestDistance = Math.abs(best.page - preferredPage);
              if (distance < bestDistance) return index;
              if (
                distance === bestDistance &&
                result.page >= preferredPage &&
                best.page < preferredPage
              ) {
                return index;
              }
              return bestIndex;
            }, 0)
          : 0;
        const targetPage = pdfResults[targetIndex].page;
        setCurrentResultIndex(targetIndex);
        setCurrentPage(targetPage);
        // Bug fix: In scroll mode, also scroll to the target page
        if (displayMode === 'scroll') {
          scrollToPage(targetPage, 'smooth', 'center');
        }
      }
    } catch (err) {
      // Bug fix: Ignore errors if search was cancelled
      if (!abortController.signal.aborted) {
        console.error('Search error:', err);
      }
    }
  }, [pdfDoc, onSearchResults, displayMode, scrollToPage]);

  // Render highlights - recalculates from PDF coordinates for current scale
  // Single mode: renders highlights for currentPage only
  // Scroll mode: renders highlights for all rendered pages
  // Bug fix: Depend on pageRenderComplete to ensure highlight layer exists before rendering
  // Bug fix: Add cancellation to prevent stale renders from modifying DOM
  useEffect(() => {
    // Track if this effect instance is cancelled
    let isCancelled = false;

    const renderHighlightsForPage = async (pageNum: number) => {
      const highlightLayer = highlightLayerRefs.current.get(pageNum);
      if (!highlightLayer || !pdfDoc) return;

      highlightLayer.innerHTML = '';

      const pageResults = pdfSearchResults.filter(r => r.page === pageNum);
      if (pageResults.length === 0) return;

      // Get page viewport for coordinate transformation
      const page = await pdfDoc.getPage(pageNum);
      if (isCancelled) return;

      const viewport = page.getViewport({ scale });

      pageResults.forEach((result) => {
        const globalIndex = pdfSearchResults.findIndex(
          r => r.page === result.page &&
               r.pdfRect[0] === result.pdfRect[0] &&
               r.pdfRect[1] === result.pdfRect[1] &&
               r.pdfRect[2] === result.pdfRect[2] &&
               r.pdfRect[3] === result.pdfRect[3]
        );

        const viewRect = viewport.convertToViewportRectangle(result.pdfRect);
        const minX = Math.min(viewRect[0], viewRect[2]);
        const minY = Math.min(viewRect[1], viewRect[3]);
        const width = Math.abs(viewRect[0] - viewRect[2]);
        const height = Math.abs(viewRect[1] - viewRect[3]);

        const highlight = document.createElement('div');
        highlight.style.position = 'absolute';
        highlight.style.left = `${minX}px`;
        highlight.style.top = `${minY}px`;
        highlight.style.width = `${width}px`;
        highlight.style.height = `${height}px`;
        highlight.style.backgroundColor = globalIndex === currentResultIndex
          ? 'rgba(255, 165, 0, 0.5)'  // Orange for current
          : 'rgba(255, 255, 0, 0.4)'; // Yellow for others
        highlight.style.mixBlendMode = 'multiply';
        highlight.style.borderBottom = '2px solid #ffcc00';
        highlight.style.pointerEvents = 'auto';
        highlight.style.cursor = 'pointer';

        highlight.onclick = () => {
          setCurrentResultIndex(globalIndex);
          // In scroll mode, scroll to the result
          if (displayMode === 'scroll') {
            scrollToPage(result.page, 'smooth', 'center');
          }
        };

        highlightLayer.appendChild(highlight);
      });
    };

    const renderHighlights = async () => {
      if (!pageRenderComplete) return;

      if (displayMode === 'scroll') {
        // Scroll mode: render highlights for all rendered pages
        const pagesToHighlight = Array.from(renderedPages);
        for (const pageNum of pagesToHighlight) {
          if (isCancelled) break;
          await renderHighlightsForPage(pageNum);
        }
      } else {
        // Single mode: render highlights for current page only
        await renderHighlightsForPage(currentPage);
      }
    };

    renderHighlights();

    return () => {
      isCancelled = true;
    };
  }, [displayMode, pdfSearchResults, currentPage, currentResultIndex, pdfDoc, scale, pageRenderComplete, renderedPages, scrollToPage]);

  // Perform initial search if highlight terms provided
  // Bug fix: Detect when highlightTerms change and re-run search
  // Bug fix: Respect initialPage parameter by not navigating to first result on initial search
  // Bug fix: Clear highlights when highlightTerms is emptied
  useEffect(() => {
    const currentTermsString = highlightTerms.join(' ');

    // Check if highlight terms have changed
    if (currentTermsString !== lastHighlightTermsRef.current) {
      // Update the search query state to stay in sync with prop
      setSearchQuery(currentTermsString);
      lastHighlightTermsRef.current = currentTermsString;
      // Reset the flag to allow new search
      initialSearchPerformedRef.current = false;

      // Bug fix: If highlightTerms is now empty but was previously non-empty, clear highlights
      if (highlightTerms.length === 0) {
        // Cancel any in-progress search
        if (searchAbortControllerRef.current) {
          searchAbortControllerRef.current.abort();
          searchAbortControllerRef.current = null;
        }
        // Clear search results to remove highlights
        setSearchResults([]);
        setPdfSearchResults([]);
        setCurrentResultIndex(0);
      } else {
        // Show search bar when highlight terms are provided via props
        setShowSearchBar(true);
      }
    }

    if (highlightTerms.length > 0 && pdfDoc && !initialSearchPerformedRef.current) {
      initialSearchPerformedRef.current = true;
      // Bug fix: Pass navigateToFirst=false when initialPage is explicitly set.
      // This respects the user's page preference from URL
      const shouldNavigateToFirst = !initialPageExplicitRef.current && initialPageRef.current <= 1;
      performSearch(currentTermsString, shouldNavigateToFirst, true);
    }
  }, [highlightTerms, pdfDoc, performSearch]);

  // Reset initial search flag when pdfUrl changes (new document)
  // Bug fix: Also clear search results to prevent stale highlights on new document
  // Note: Search abort is now handled in loadPDF cleanup to ensure it runs before PDF destruction
  useEffect(() => {
    initialSearchPerformedRef.current = false;
    lastHighlightTermsRef.current = '';
    // Clear is also done in loadPDF effect, but this ensures cleanup on URL change before new load
    setSearchResults([]);
    setPdfSearchResults([]);
  }, [pdfUrl]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const nextPage = currentPage - 1;
      setCurrentPage(nextPage);
      if (displayMode === 'scroll') {
        scrollToPage(nextPage, 'smooth', 'start');
      }
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      if (displayMode === 'scroll') {
        scrollToPage(nextPage, 'smooth', 'start');
      }
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const handlePrevResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const newIndex = currentResultIndex > 0 ? currentResultIndex - 1 : searchResults.length - 1;
    setCurrentResultIndex(newIndex);
    const targetPage = searchResults[newIndex].page;
    setCurrentPage(targetPage);
    // In scroll mode, scroll to the target page
    if (displayMode === 'scroll') {
      scrollToPage(targetPage, 'smooth', 'center');
    }
  }, [searchResults, currentResultIndex, displayMode, scrollToPage]);

  const handleNextResult = useCallback(() => {
    if (searchResults.length === 0) return;
    const newIndex = currentResultIndex < searchResults.length - 1 ? currentResultIndex + 1 : 0;
    setCurrentResultIndex(newIndex);
    const targetPage = searchResults[newIndex].page;
    setCurrentPage(targetPage);
    // In scroll mode, scroll to the target page
    if (displayMode === 'scroll') {
      scrollToPage(targetPage, 'smooth', 'center');
    }
  }, [searchResults, currentResultIndex, displayMode, scrollToPage]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(searchQuery);
  };

  // Handle result click from sidebar
  const handleResultClick = (index: number) => {
    setCurrentResultIndex(index);
    const targetPage = searchResults[index].page;
    setCurrentPage(targetPage);
    // In scroll mode, scroll to the target page
    if (displayMode === 'scroll') {
      scrollToPage(targetPage, 'smooth', 'center');
    }
  };

  // Keyboard shortcuts for navigation (N = next, P = previous)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts if not typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (searchResults.length === 0) return;

      switch (e.key.toLowerCase()) {
        case 'n':
          // Next result
          handleNextResult();
          break;
        case 'p':
          // Previous result
          handlePrevResult();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [searchResults.length, handleNextResult, handlePrevResult]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 1,
          marginBottom: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Navigation controls */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={handlePrevPage} disabled={currentPage <= 1} size="small">
            <PrevIcon />
          </IconButton>
          <Typography variant="body2">
            {currentPage} / {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={currentPage >= totalPages} size="small">
            <NextIcon />
          </IconButton>
        </Box>

        {/* Zoom controls */}
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={handleZoomOut} disabled={scale <= 0.5} size="small">
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 50, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>
          <IconButton onClick={handleZoomIn} disabled={scale >= 3} size="small">
            <ZoomInIcon />
          </IconButton>
        </Box>

        {/* Search toggle and results list toggle */}
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title={t('documents.pdfSearchTooltip')}>
            <IconButton onClick={() => setShowSearchBar(!showSearchBar)} size="small">
              <SearchIcon />
            </IconButton>
          </Tooltip>
          {searchResults.length > 0 && (
            <Tooltip title={t('documents.toggleResultsList')}>
              <IconButton
                onClick={() => setShowResultsSidebar(!showResultsSidebar)}
                size="small"
                color={showResultsSidebar ? 'primary' : 'default'}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>

      {/* Search bar */}
      {showSearchBar && (
        <Paper
          elevation={1}
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 1,
            marginBottom: 1,
            gap: 1,
          }}
        >
          <TextField
            size="small"
            placeholder={t('documents.searchInDocument')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1 }}
          />
          <IconButton
            type="submit"
            size="small"
          >
            <SearchIcon />
          </IconButton>
          {searchResults.length > 0 && (
            <>
              <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
                {currentResultIndex + 1} / {searchResults.length}
              </Typography>
              <IconButton onClick={handlePrevResult} size="small">
                <PrevIcon />
              </IconButton>
              <IconButton onClick={handleNextResult} size="small">
                <NextIcon />
              </IconButton>
            </>
          )}
          <IconButton onClick={() => setShowSearchBar(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Paper>
      )}

      {/* PDF Container with Minimap */}
      <Box sx={{ display: 'flex', flex: 1, position: 'relative', minHeight: 500 }}>
        {/* PDF Container */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#525659',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
          }}
        />

        {/* Minimap - shows match positions in document */}
        {searchResults.length > 0 && totalPages > 0 && (
          <Box
            sx={{
              width: 24,
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              position: 'relative',
              flexShrink: 0,
              borderLeft: '1px solid rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Current page indicator */}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${((currentPage - 1) / totalPages) * 100}%`,
                height: `${(1 / totalPages) * 100}%`,
                backgroundColor: 'rgba(100, 149, 237, 0.3)',
                borderTop: '1px solid rgba(100, 149, 237, 0.8)',
                borderBottom: '1px solid rgba(100, 149, 237, 0.8)',
                pointerEvents: 'none',
              }}
            />
            {/* Match markers */}
            {searchResults.map((result, index) => {
              const position = ((result.page - 1) / totalPages) * 100;
              return (
                <Tooltip
                  key={index}
                  title={t('documents.pageResultPreview', {
                    page: result.page,
                    text: result.text.substring(0, 30),
                  })}
                  placement="left"
                >
                  <Box
                    onClick={() => handleResultClick(index)}
                    sx={{
                      position: 'absolute',
                      left: 2,
                      right: 2,
                      top: `${position}%`,
                      height: 4,
                      backgroundColor: index === currentResultIndex
                        ? 'rgba(255, 165, 0, 0.9)'
                        : 'rgba(255, 255, 0, 0.8)',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 165, 0, 1)',
                        transform: 'scaleY(1.5)',
                      },
                      transition: 'all 0.15s ease',
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Results Sidebar Drawer */}
      <Drawer
        anchor="right"
        open={showResultsSidebar}
        onClose={() => setShowResultsSidebar(false)}
        variant="persistent"
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            position: 'absolute',
            height: '100%',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('search.searchResults')} ({searchResults.length})
          </Typography>
          <IconButton size="small" onClick={() => setShowResultsSidebar(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Tooltip title={t('documents.previousResultShortcut')}>
            <IconButton size="small" onClick={handlePrevResult} disabled={searchResults.length === 0}>
              <UpIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
            {currentResultIndex + 1} / {searchResults.length}
          </Typography>
          <Tooltip title={t('documents.nextResultShortcut')}>
            <IconButton size="small" onClick={handleNextResult} disabled={searchResults.length === 0}>
              <DownIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {searchResults.map((result, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton
                selected={index === currentResultIndex}
                onClick={() => handleResultClick(index)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 165, 0, 0.2)',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: index === currentResultIndex ? 'bold' : 'normal' }}>
                      {t('pagination.page')} {result.page}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {result.text}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Box>
  );
};

export default PDFHighlightViewer;
